import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { InputHelper } from '../../../../utils/input.helper';
import { MessageService, OverlayPanel, SelectItem } from 'primeng';
import { ICancelData } from '../../../../interfaces/job/job';
import { Message } from '../../../../classes/Message';
import { OrderService } from '../../../../services';
import { ICancellationStatus } from '../../../../interfaces/cancellation-status';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ControlsOf } from '../../../../utils/controls-of';

const defaultSelectItem: SelectItem = {
  label: 'Please Select',
  value: null,
};

interface CancelForm {
  newCustomerChargedPrice: number;
  newSupplierChargedPrice: number;
  cancellationStatusId: number;
  cancellationDescription: string;
  secondaryCancellationStatusId: number;
  copyOrder: boolean;
}

@Component({
  selector: 'vme-cancel-order-dialog',
  templateUrl: './vme-cancel-order-dialog.component.html',
})
export class VmeCancelOrderDialogComponent {
  constructor(
    public inputHelper: InputHelper,
    public orderService: OrderService,
    public messageService: MessageService,
  ) {}

  @Input() visible: boolean = false;

  @Input() jobId: number;

  @Input() orderId: number;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() onCancelled: EventEmitter<void> = new EventEmitter<void>();

  // @ViewChild('copyOrderPanel') copyOrderPanel: OverlayPanel;

  _loading: boolean = false;

  get loading(): boolean {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = value;
  }

  customerChargedPrice: number;
  supplierChargedPrice: number;

  cancellationStatuses: ICancellationStatus[] = [];

  cancellationStatusOptions: SelectItem[] = [];
  secondaryCancellationStatusOptions: SelectItem[] = [];
  displaySecondaryCancellation: boolean = false;

  formGroup: FormGroup<ControlsOf<CancelForm>> = new FormGroup<ControlsOf<CancelForm>>({
    newCustomerChargedPrice: new FormControl<number>(null, Validators.min(0)),
    newSupplierChargedPrice: new FormControl<number>(null, Validators.min(0)),
    cancellationStatusId: new FormControl<number>(null, Validators.required),
    cancellationDescription: new FormControl<string>(null, Validators.required),
    secondaryCancellationStatusId: new FormControl<number>(null, this.secondaryStatusValidator()),
    copyOrder: new FormControl<boolean>(false),
  });

  public get selectedCancellationStatus(): ICancellationStatus {
    return this.cancellationStatuses.find(
      (c) => c.id === this.formGroup.controls.cancellationStatusId.value,
    );
  }

  async cancelOrder(withFee: boolean): Promise<void> {
    const data: ICancelData = {
      isFee: withFee,
      customerChargedPrice: this.customerChargedPrice,
      supplierChargedPrice: this.supplierChargedPrice,
      jobId: this.jobId,
      orderId: this.orderId,
      ...this.formGroup.getRawValue(),
    };

    try {
      this.loading = true;
      await this.orderService.cancelOrder(data);
      this.messageService.add(Message.Success('Order cancelled', 'Success'));
      if (this.formGroup.controls.copyOrder.value) {
        await this.copyOrder(this.orderId);
      }
      this.onCancelled.emit();
    } catch (error) {
      this.messageService.add(Message.Error('Error cancelling order', 'Error'));
    } finally {
      this.loading = false;
    }
  }

  async copyOrder(orderId: number) {
    try {
      const result = await this.orderService.quickCopy(orderId);
      this.messageService.add(Message.Success('Order copied', 'Success'));
      window.open(`/app/jobscreen/${result.jobId}`, '_blank');
    } catch (error) {
      this.messageService.add(Message.Error('Error copying order', 'Error'));
    }
  }

  onCancellationStatusChange() {
    if (!this.selectedCancellationStatus) {
      this.hideSecondaryCancellationInput();
      return;
    }

    const secondaryStatuses = this.selectedCancellationStatus.secondaryCancellationStatuses.filter(
      (x) => x.isActive,
    );

    if (secondaryStatuses.length == 0) {
      this.hideSecondaryCancellationInput();
      return;
    }

    this.secondaryCancellationStatusOptions = [defaultSelectItem].concat(
      secondaryStatuses.map((x) => ({ label: x.name, value: x.id })),
    );

    this.displaySecondaryCancellation = true;
  }

  hideSecondaryCancellationInput() {
    this.displaySecondaryCancellation = false;
    this.formGroup.controls.secondaryCancellationStatusId.setValue(null);
  }

  async populateCancellationStatuses(): Promise<void> {
    const result = await this.orderService.getCancellationStatuses();
    this.cancellationStatuses = result.filter((x) => x.isActive);
    this.cancellationStatusOptions = [defaultSelectItem].concat(
      this.cancellationStatuses.map((x) => ({ label: x.name, value: x.id, tag: x })),
    );
  }

  async getCancelOrderInfo() {
    const res = await this.orderService.getCancelOrderInfo(this.orderId);

    this.formGroup.patchValue({
      newCustomerChargedPrice: res.customerChargedPrice,
      newSupplierChargedPrice: res.supplierChargedPrice,
    });

    this.customerChargedPrice = res.customerChargedPrice;
    this.supplierChargedPrice = res.supplierChargedPrice;
  }

  async onShow() {
    this.loading = true;
    this.displaySecondaryCancellation = false;
    this.formGroup.reset();
    await this.populateCancellationStatuses();
    await this.getCancelOrderInfo();
    this.loading = false;
  }

  private secondaryStatusValidator(): ValidatorFn {
    return (control: FormControl) => {
      if (!this.displaySecondaryCancellation) {
        return null;
      }

      if (control.value) {
        return null;
      }

      return { required: {} };
    };
  }
}
